import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import api, { formatApiError } from "../../lib/api";
import { fetchThumb } from "../../lib/thumbCache";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Share2, Loader2, FileText, ZoomIn, ZoomOut } from "lucide-react";
import RecipientPicker from "./RecipientPicker";
import AttachmentPreviewModal from "../AttachmentPreviewModal";
import { modSelect, noShiftTextSelect } from "../../lib/mouseSelect";

export const CAT_LABEL = { entrata: "Documenti in Entrata", uscita: "Documenti in Uscita", avvocato: "Allegati Avvocato", perito: "Allegati Perito",
  cessionario: "Allegati Cessionario", fornitore: "Allegati Fornitore", assistito: "Allegati Assistito" };
const RUOLI = ["Perito", "Liquidatore", "Avvocato", "Ispettore", "Medico legale", "Carrozziere"];

const SIZES = [48, 96, 160, 240];

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";
// Miniatura PDF lato browser (prima pagina) quando manca quella del server: max 2 in parallelo.
const pdfThumbs = new Map();
let pdfActive = 0;
const pdfQueue = [];
const pdfNext = () => { while (pdfActive < 2 && pdfQueue.length) { const job = pdfQueue.shift(); pdfActive++; job().finally(() => { pdfActive--; pdfNext(); }); } };
async function renderPdfFirstPage(pid, aid) {
  let buf;
  const { data } = await api.get(`/studio/pratiche/${pid}/allegati-files/${aid}/content-url`).catch(() => ({ data: {} }));
  if (data?.url) buf = await (await fetch(data.url)).arrayBuffer();
  else buf = (await api.get(`/studio/pratiche/${pid}/allegati-files/${aid}/content`, { responseType: "arraybuffer" })).data;
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  try {
    const page = await pdf.getPage(1);
    const v0 = page.getViewport({ scale: 1 });
    const vp = page.getViewport({ scale: 360 / Math.max(v0.width, v0.height) });
    const c = document.createElement("canvas");
    c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    return c.toDataURL("image/jpeg", 0.75);
  } finally { pdf.destroy(); }
}
function pdfThumb(pid, aid) {
  if (!pdfThumbs.has(aid)) {
    pdfThumbs.set(aid, new Promise((resolve) => { pdfQueue.push(() => renderPdfFirstPage(pid, aid).then(resolve, () => resolve(null))); pdfNext(); }));
  }
  return pdfThumbs.get(aid);
}
const BLUE = "text-[#60a5fa] [html.light_&]:text-[#1e3a8a]";
const folderOf = (a) => a.cartella || CAT_LABEL[a.categoria] || a.categoria || "";
const FIXED_COLORS = { "foto veicolo": "#15803d", "documenti in entrata": "#1e3a8a", "allegati assistito": "#c2410c", "foto luogo sinistro": "#6d28d9",
  "foto documenti": "#0f766e", "documenti in uscita": "#991b1b", "caricati dal cliente": "#be185d" };
const PALETTE = ["#0369a1", "#a16207", "#4d7c0f", "#7e22ce", "#b45309", "#0e7490", "#9f1239", "#3f6212", "#4338ca", "#57534e"];
const FolderName = ({ a, cls, color }) => <span className={`inline-block mt-0.5 rounded px-1.5 py-0.5 font-medium text-white ${cls}`} style={{ backgroundColor: color || "#1e3a8a" }} data-testid={`share-file-folder-${a.id}`}>{folderOf(a)}</span>;
const isPdfFile = (a) => /pdf/i.test(a.content_type || "") || /\.pdf$/i.test(a.nome || "");
const SIZE_KEY = "pgp_share_thumb_size";

const Thumb = ({ pid, a, size = 48 }) => {
  const [url, setUrl] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  useEffect(() => {
    let on = true;
    const pdf = isPdfFile(a);
    if (/^image\//i.test(a.content_type) || pdf) {
      fetchThumb(pid, a.id, a.content_type).catch(() => null).then((u) => {
        if (!on) return;
        if (u) setUrl(u);
        else if (pdf && visible) pdfThumb(pid, a.id).then((d) => on && d && setUrl(d));
      });
    }
    return () => { on = false; };
  }, [pid, a, visible]);
  const st = size > 48 ? { width: "100%", height: size } : { width: size, height: size };
  return url ? <img src={url} alt="" style={st} data-testid={`share-thumb-img-${a.id}`} className={`rounded border border-slate-700 shrink-0 ${size > 48 ? "object-contain bg-slate-900/40" : "object-cover"}`} />
    : <div ref={ref} style={st} className="rounded border border-slate-700 bg-slate-800/60 flex items-center justify-center shrink-0"><FileText className={size > 96 ? "h-10 w-10 text-slate-400" : "h-5 w-5 text-slate-400"} /></div>;
};

export default function ShareCreateDialog({ open, onOpenChange, praticaId, onCreated }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState([]);
  const [f, setF] = useState({ nome: "", cognome: "", ruolo: "", email: "", modalita: "giorni", giorni: 5, note: "", otp: true });
  const [busy, setBusy] = useState(false);
  const [tsize, setTsize] = useState(() => { const v = parseInt(localStorage.getItem(SIZE_KEY), 10); return SIZES.includes(v) ? v : 48; });
  const zoom = (d) => setTsize((s) => { const i = Math.min(SIZES.length - 1, Math.max(0, SIZES.indexOf(s) + d)); localStorage.setItem(SIZE_KEY, String(SIZES[i])); return SIZES[i]; });
  const gridCls = tsize <= 48 ? "sm:grid-cols-2" : tsize <= 96 ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-4" : tsize <= 160 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2";
  const [fCart, setFCart] = useState("");
  const [prevIdx, setPrevIdx] = useState(-1);
  const prevOpen = prevIdx >= 0;
  useEffect(() => {
    if (!prevOpen) return;
    const b = document.body, old = b.style.pointerEvents;
    b.style.pointerEvents = "auto";
    return () => { b.style.pointerEvents = old; };
  }, [prevOpen]);
  useEffect(() => {
    if (!open) return;
    setSel([]); setFCart(""); setF({ nome: "", cognome: "", ruolo: "", email: "", modalita: "giorni", giorni: 5, note: "", otp: true });
    api.get(`/studio/pratiche/${praticaId}/condivisioni/allegati`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [open, praticaId]);
  const folders = useMemo(() => {
    const c = {};
    items.forEach((a) => { const k = folderOf(a); c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).sort((x, y) => x[0].localeCompare(y[0], "it"));
  }, [items]);
  const folderColor = useMemo(() => {
    const m = {};
    let i = 0;
    folders.forEach(([k]) => { m[k] = FIXED_COLORS[k.trim().toLowerCase()] || PALETTE[i++ % PALETTE.length]; });
    return m;
  }, [folders]);
  const visible = useMemo(() => (fCart ? items.filter((a) => folderOf(a) === fCart) : items), [items, fCart]);
  const allVisSel = visible.length > 0 && visible.every((a) => sel.includes(a.id));
  const prevFiles = useMemo(() => visible.map((a) => (isPdfFile(a) ? { ...a, content_type: "application/pdf" } : a)), [visible]);
  const openPrev = (a) => (e) => { e.preventDefault(); e.stopPropagation(); setPrevIdx(visible.findIndex((x) => x.id === a.id)); };
  const modClick = (id) => (e) => modSelect(e, id, visible.map((x) => x.id), lastSelRef, flipSel, (ids) => setSel((s) => [...new Set([...s, ...ids])]));
  const toggleAll = () => {
    const ids = visible.map((a) => a.id);
    setSel((s) => (allVisSel ? s.filter((x) => !ids.includes(x)) : [...new Set([...s, ...ids])]));
  };
  const groups = useMemo(() => {
    const g = {};
    visible.forEach((a) => { (g[a.categoria] = g[a.categoria] || []).push(a); });
    return Object.entries(g);
  }, [visible]);
  const lastSelRef = useRef(null);
  const flipSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggle = (id) => { lastSelRef.current = id; flipSel(id); };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const send = async () => {
    if (!sel.length) return toast.error("Seleziona almeno un allegato");
    setBusy(true);
    try {
      await api.post(`/studio/pratiche/${praticaId}/condivisioni`, { ...f, giorni: parseInt(f.giorni, 10) || 0, allegati: sel, app_url: window.location.origin });
      toast.success(`Link inviato a ${f.email}`);
      onCreated?.(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="pgp-modal bg-[#121824] border-slate-700 text-slate-100 max-w-4xl max-h-[90vh] overflow-y-auto lg:h-[92vh] lg:max-h-[92vh] lg:flex lg:flex-col lg:overflow-hidden" data-testid="share-create-dialog"
        onInteractOutside={(e) => { if (prevOpen) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (prevOpen) { e.preventDefault(); setPrevIdx(-1); } }}>
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Share2 className="h-4 w-4 text-amber-400" /> Condivisione Allegati</DialogTitle></DialogHeader>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-5 lg:flex-1 lg:min-h-0">
          <div className="min-w-0 lg:flex lg:flex-col lg:min-h-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-slate-400">Allegati della pratica ({sel.length} selezionati)</span>
              <div className="flex items-center gap-3">
                {folders.length > 1 && (
                  <Select value={fCart || "__all"} onValueChange={(v) => setFCart(v === "__all" ? "" : v)}>
                    <SelectTrigger className="h-7 w-52 text-xs bg-slate-900/60 border-slate-700" data-testid="share-folder-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all" data-testid="share-folder-opt-all">Cartella: Tutte ({items.length})</SelectItem>
                      {folders.map(([k, n]) => <SelectItem key={k} value={k} data-testid={`share-folder-opt-${k}`}>{k} ({n})</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center rounded-md border border-slate-700 overflow-hidden" title="Dimensione miniature">
                  <button type="button" onClick={() => zoom(-1)} disabled={tsize === SIZES[0]} data-testid="share-thumb-smaller" title="Riduci miniature"
                    className="px-2 py-1 text-slate-300 hover:bg-slate-800 disabled:opacity-40 transition-colors"><ZoomOut className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => zoom(1)} disabled={tsize === SIZES[SIZES.length - 1]} data-testid="share-thumb-bigger" title="Ingrandisci miniature"
                    className="px-2 py-1 text-slate-300 hover:bg-slate-800 border-l border-slate-700 disabled:opacity-40 transition-colors"><ZoomIn className="h-3.5 w-3.5" /></button>
                </div>
                {visible.length > 0 && <button type="button" className="text-xs text-amber-400 hover:underline" data-testid="share-select-all"
                  onClick={toggleAll}>{allVisSel ? "Deseleziona tutti" : "Seleziona tutti"}</button>}
              </div>
            </div>
            {items.length === 0 && <p className="text-sm text-slate-500 py-6 text-center" data-testid="share-no-files">Nessun allegato nella pratica.</p>}
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 lg:max-h-none lg:flex-1 lg:min-h-0" data-testid="share-files-scroll">
              {groups.map(([cat, list]) => (
                <div key={cat}>
                  <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${BLUE}`} data-testid={`share-cat-${cat}`}>{CAT_LABEL[cat] || cat}</div>
                  <div className={`grid gap-2 ${gridCls}`} data-testid="share-files-grid">
                    {list.map((a) => tsize <= 48 ? (
                      <label key={a.id} data-testid={`share-file-${a.id}`} onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect} className={`flex items-center gap-3 rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${sel.includes(a.id) ? "border-amber-500/60 bg-amber-500/10" : "border-slate-800 hover:bg-slate-800/40"}`}>
                        <Checkbox checked={sel.includes(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`share-file-check-${a.id}`} />
                        <span role="button" tabIndex={0} onClick={openPrev(a)} title="Apri anteprima" data-testid={`share-thumb-open-${a.id}`} className="shrink-0 cursor-zoom-in"><Thumb pid={praticaId} a={a} size={tsize} /></span>
                        <span className="min-w-0"><span className="text-sm text-slate-200 break-all line-clamp-2">{a.nome}</span><FolderName a={a} cls="text-xs" color={folderColor[folderOf(a)]} /></span>
                      </label>
                    ) : (
                      <label key={a.id} data-testid={`share-file-${a.id}`} onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect} className={`flex flex-col gap-2 rounded-lg border p-2 cursor-pointer transition-colors ${sel.includes(a.id) ? "border-amber-500/60 bg-amber-500/10" : "border-slate-800 hover:bg-slate-800/40"}`}>
                        <span role="button" tabIndex={0} onClick={openPrev(a)} title="Apri anteprima" data-testid={`share-thumb-open-${a.id}`} className="block cursor-zoom-in"><Thumb pid={praticaId} a={a} size={tsize} /></span>
                        <span className="flex items-start gap-2">
                          <Checkbox checked={sel.includes(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`share-file-check-${a.id}`} />
                          <span className="min-w-0"><span className="text-xs text-slate-200 break-all line-clamp-2">{a.nome}</span><FolderName a={a} cls="text-[11px]" color={folderColor[folderOf(a)]} /></span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3 lg:min-h-0 lg:overflow-y-auto lg:pr-1" data-testid="share-side-scroll">
            {open && <RecipientPicker praticaId={praticaId} onPick={(r) => setF((x) => ({ ...x, nome: r.nome || "", cognome: r.cognome || "", ruolo: r.ruolo || "", email: r.email || "" }))} />}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-slate-300">Nome *</Label><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="share-nome" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
              <div><Label className="text-xs text-slate-300">Cognome</Label><Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="share-cognome" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
            </div>
            <div><Label className="text-xs text-slate-300">Ruolo</Label>
              <Input list="share-ruoli" value={f.ruolo} onChange={(e) => set("ruolo", e.target.value)} placeholder="Es. Perito, Liquidatore" data-testid="share-ruolo" className="mt-1 bg-slate-900/60 border-slate-700" />
              <datalist id="share-ruoli">{RUOLI.map((r) => <option key={r} value={r} />)}</datalist></div>
            <div><Label className="text-xs text-slate-300">Email *</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="share-email" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
            <div className="rounded-lg border border-slate-800 p-3 space-y-2">
              <div className="text-xs uppercase tracking-wide text-slate-400">Scadenza del link</div>
              <label className="flex items-center gap-2 text-sm text-slate-200"><input type="radio" checked={f.modalita === "singolo"} onChange={() => set("modalita", "singolo")} data-testid="share-mode-singolo" /> Accesso singolo (scade dopo la prima apertura)</label>
              <label className="flex items-center gap-2 text-sm text-slate-200"><input type="radio" checked={f.modalita === "giorni"} onChange={() => set("modalita", "giorni")} data-testid="share-mode-giorni" /> Scadenza dopo
                <Input type="number" min={1} max={365} value={f.giorni} disabled={f.modalita !== "giorni"} onChange={(e) => set("giorni", e.target.value)} data-testid="share-giorni" className="w-20 h-8 bg-slate-900/60 border-slate-700" /> giorni</label>
            </div>
            <label className="flex items-start gap-2 rounded-lg border border-slate-800 p-3 cursor-pointer" data-testid="share-otp-option">
              <Checkbox checked={!!f.otp} onCheckedChange={(v) => set("otp", !!v)} data-testid="share-otp" className="mt-0.5" />
              <span><span className="block text-sm text-slate-200 font-semibold">Verifica OTP</span>
                <span className="block text-xs text-slate-400">All'apertura del link il destinatario riceve via email un codice da inserire prima di vedere i file.</span></span>
            </label>
            <div><Label className="text-xs text-slate-300">Note per il destinatario</Label>
              <textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={4} maxLength={5000} placeholder="Messaggio visibile al destinatario quando apre il link" data-testid="share-note"
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700" data-testid="share-cancel">Annulla</Button>
          <Button onClick={send} disabled={busy || !sel.length || !f.nome.trim() || !f.email.trim()} data-testid="share-send" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Share2 className="h-4 w-4 mr-1" />} Invia link
          </Button>
        </DialogFooter>
      </DialogContent>
      {prevIdx >= 0 && prevFiles[prevIdx] && (
        <AttachmentPreviewModal files={prevFiles} index={prevIdx} onIndex={setPrevIdx} pid={praticaId} onClose={() => setPrevIdx(-1)} />
      )}
    </Dialog>
  );
}
